import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { TrendingUp, ArrowLeft, Inbox, PhoneCall, CheckCircle2, Star } from "lucide-react";
import { DateRangeFilter } from "@/components/DateRangeFilter";

const Stat = ({ icon: Icon, label, value, suffix, testid, accent }) => (
  <div className="card-soft" data-testid={testid}>
    <div className="flex items-center gap-2 text-[#5C6B64] text-xs uppercase tracking-wide">
      <Icon className={`w-4 h-4 ${accent || "text-[#2D6A4F]"}`} /> {label}
    </div>
    <div className="font-heading text-3xl text-[#1E3F33] mt-2">{value}{suffix || ""}</div>
  </div>
);

export default function LeadInsightsPage() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState({ start: "", end: "" });

  const fetchData = useCallback((start, end) => {
    setData(null);
    api.get("/leads/insights", { params: { start: start || undefined, end: end || undefined } })
      .then(({ data }) => setData(data))
      .catch(() => setData({ total: 0, contacted: 0, confirmed: 0, reviewed: 0, conversion_rate: 0 }));
  }, []);

  useEffect(() => { fetchData(range.start, range.end); }, [range, fetchData]);

  return (
    <div className="container-x py-12 max-w-4xl" data-testid="lead-insights-page">
      <Link to="/dashboard/collector" className="inline-flex items-center gap-1.5 text-sm text-[#5C6B64] hover:text-[#CC5A3A] mb-6" data-testid="insights-back-link">
        <ArrowLeft className="w-4 h-4" /> Back to dashboard
      </Link>
      <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold flex items-center gap-2">
        <TrendingUp className="w-6 h-6 text-[#2D6A4F]" /> Lead insights
      </h1>
      <p className="text-[#5C6B64] mt-2 mb-6">See how your customer leads are converting — total leads, how many you contacted, and how many customers confirmed contact.</p>

      <div className="mb-8"><DateRangeFilter onChange={(start, end) => setRange({ start, end })} /></div>

      {data === null ? (
        <div className="text-[#5C6B64]">Loading…</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={Inbox} label="Total leads" value={data.total} testid="insight-total" accent="text-[#CC5A3A]" />
          <Stat icon={PhoneCall} label="Contacted" value={data.contacted} testid="insight-contacted" />
          <Stat icon={CheckCircle2} label="Confirmed" value={data.confirmed} testid="insight-confirmed" />
          <Stat icon={TrendingUp} label="Conversion" value={data.conversion_rate} suffix="%" testid="insight-conversion" />
        </div>
      )}

      {data && data.total > 0 && (
        <p className="text-sm text-[#8A9A86] mt-6 inline-flex items-center gap-1.5" data-testid="insight-reviewed">
          <Star className="w-4 h-4 text-[#CC5A3A]" /> {data.reviewed} of your confirmed leads left a review.
        </p>
      )}
    </div>
  );
}
